import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../client";
import type { Page, Paged, Product, ProductFacets, ProductSummary, Review, Store } from "../types";
import { createOptimisticHandlers } from "./optimistic";
import { qk } from "./qk";
import type { ProductListParams } from "./qk";

function buildProductListQuery(params: ProductListParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.category) search.set("category", params.category);
  if (params.subcategory) search.set("subcategory", params.subcategory);
  if (params.subscope) search.set("subscope", params.subscope);
  if (params.gameType) search.set("gameType", params.gameType);
  if (params.publishedWithin) search.set("publishedWithin", String(params.publishedWithin));
  if (params.price) search.set("price", params.price);
  if (params.minRating !== undefined) search.set("minRating", String(params.minRating));
  if (params.sort) search.set("sort", params.sort);
  search.set("page", String(params.page ?? 1));
  search.set("pageSize", String(params.pageSize ?? 48));
  return search.toString();
}

export const productsQuery = (params: ProductListParams = {}) =>
  queryOptions({
    queryKey: qk.products.list(params),
    queryFn: () =>
      apiGet<Paged<ProductSummary> & { facets: ProductFacets }>(
        `/api/products?${buildProductListQuery(params)}`,
      ),
    staleTime: 120_000, // plan §5: catalog list
    placeholderData: keepPreviousData,
  });

export const productsInfiniteQuery = (params: ProductListParams = {}) =>
  infiniteQueryOptions({
    queryKey: qk.products.infinite(params),
    queryFn: ({ pageParam }: { pageParam: number }) =>
      apiGet<Paged<ProductSummary> & { facets: ProductFacets }>(
        `/api/products?${buildProductListQuery({ ...params, page: pageParam, pageSize: 48 })}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage: Paged<ProductSummary>) => {
      const loadedPages = lastPage.page * lastPage.pageSize;
      return loadedPages < lastPage.total ? lastPage.page + 1 : undefined;
    },
    staleTime: 120_000,
    placeholderData: keepPreviousData,
  });

export const productQuery = (slug: string) =>
  queryOptions({
    queryKey: qk.products.detail(slug),
    queryFn: () => apiGet<Product & { store: Store }>(`/api/products/${slug}`),
    staleTime: 300_000, // plan §5: entity detail
  });

export const productReviewsInfiniteQuery = (slug: string) =>
  infiniteQueryOptions({
    queryKey: qk.products.reviews(slug),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      apiGet<Page<Review>>(`/api/products/${slug}/reviews?limit=20${pageParam ? `&cursor=${pageParam}` : ""}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: Page<Review>) => lastPage.nextCursor,
    staleTime: 300_000,
  });

export const relatedProductsQuery = (slug: string) =>
  queryOptions({
    queryKey: qk.products.related(slug),
    queryFn: () => apiGet<ProductSummary[]>(`/api/products/${slug}/related`),
    staleTime: 300_000,
  });

export interface ToggleWishlistVariables {
  slug: string;
  inWishlist: boolean;
}

export function useToggleWishlist() {
  return useMutation(toggleWishlistMutationOptions(useQueryClient()));
}

/** The wishlist mutation's options, outside the hook so the cache behaviour is unit-tested. */
export function toggleWishlistMutationOptions(queryClient: QueryClient) {
  return {
    mutationFn: ({ slug, inWishlist }: ToggleWishlistVariables) =>
      inWishlist
        ? apiPost<{ inWishlist: boolean }>(`/api/products/${slug}/wishlist`)
        : apiDelete<{ inWishlist: boolean }>(`/api/products/${slug}/wishlist`),
    ...createOptimisticHandlers<ToggleWishlistVariables>({
      queryClient,
      action: "toggle-wishlist",
      affectedKeys: () => [],
      // Exact: a toggle must not cancel or refetch the reviews and related lists nested under the detail key.
      exactAffectedKeys: ({ slug }) => [qk.products.detail(slug)],
      apply: ({ slug, inWishlist }) => [
        {
          queryKey: qk.products.detail(slug),
          updater: (previous: Product | undefined) => (previous ? { ...previous, inWishlist } : previous),
        },
      ],
    }),
  };
}
